import { useMemo, useState } from 'react'
import { create } from '@bufbuild/protobuf'
import { useStore } from '@tanstack/react-store'
import { Plus } from 'lucide-react'
import { toast } from 'sonner'

import { CreateTaskRequestSchema } from '@/gen/cortado/v1/cortado_pb'
import { TaskSchema, type Task, type TaskLite } from '@/gen/cortado/v1/actions_pb'
import type { Job } from '@/gen/cortado/v1/jobs_pb'
import { ParentTypeEnum, TaskStatusEnum } from '@/gen/cortado/v1/settings_pb'
import { dateToDateOnlyTimestamp } from '@/lib/date-utils'
import { getErrorDescription } from '@/lib/error-utils'
import { reportError } from '@/lib/error-reporting'
import { cn, formatFullName } from '@/lib/utils'
import { organizationStore } from '@/stores/organization.store'
import { useInvalidateJobActivity } from '@/stores/pipeline.store'
import { useCreateTask, useTasksList, useUpdateTask } from '@/stores/tasks.store'
import { useCurrentUser } from '@/stores/user.store'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Skeleton } from '@/components/ui/skeleton'
import { jobTaskMetaParts } from '@/components/pipeline/job-detail-view'
import { JobTaskComposer, type JobTaskDraft } from '@/components/pipeline/job-task-composer'
import { TaskSheet } from '@/components/tasks/task-sheet'

const OPEN_STATUSES = [TaskStatusEnum.TASK_STATUS_NOT_STARTED, TaskStatusEnum.TASK_STATUS_IN_PROGRESS]

/**
 * The job's open tasks. Tasks are parented to the job itself, so the backend
 * stamps `job_id` on create and `ListTasks` filters on it.
 */
type SheetState = { task: Task } | { draft: JobTaskDraft }

/** An edit-mode `Task` for the sheet, from the list's lightweight row. */
function taskFromLite(row: TaskLite): Task {
  return create(TaskSchema, {
    id: row.id,
    organizationId: row.organizationId,
    parentType: row.parentType,
    parentId: row.parentId,
    assignedTo: row.assignedTo,
    dueDate: row.dueDate,
    taskStatus: row.taskStatus,
    title: row.title,
    description: row.description,
  })
}

/**
 * The job's open tasks. Tasks are parented to the job itself, so the backend
 * stamps `job_id` on create and `ListTasks` filters on it.
 *
 * With `eventId` it is the event's own task list instead ("Tasks for this
 * event"): tasks parented to that event, created on it.
 *
 * @param eventDay - the job's earliest event day, for the composer's event-relative due picks
 */
export function JobTasksCard({ job, eventDay, eventId }: { job: Job; eventDay?: Date; eventId?: string }) {
  const selectedOrganizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)
  const { user } = useCurrentUser()
  const parent = eventId
    ? { parentType: ParentTypeEnum.PARENT_TYPE_EVENT, parentId: eventId, label: 'This event' }
    : { parentType: ParentTypeEnum.PARENT_TYPE_JOB, parentId: job.id, label: 'This job' }
  const { tasks, totalCount, hasLoaded } = useTasksList(
    eventId
      ? { parentType: ParentTypeEnum.PARENT_TYPE_EVENT, parentId: eventId, statuses: OPEN_STATUSES }
      : { jobId: job.id, statuses: OPEN_STATUSES },
  )
  const { updateTaskStatus } = useUpdateTask()
  const { mutation: createMutation } = useCreateTask()
  const invalidateJobActivity = useInvalidateJobActivity()

  const [isAdding, setIsAdding] = useState(false)
  const [sheet, setSheet] = useState<SheetState | null>(null)
  const [completingIds, setCompletingIds] = useState<ReadonlySet<string>>(new Set())

  const openTasks = useMemo(() => tasks.filter((task) => !completingIds.has(task.id)), [tasks, completingIds])

  async function completeTask(taskId: string) {
    setCompletingIds((ids) => new Set(ids).add(taskId))
    try {
      await updateTaskStatus(taskId, TaskStatusEnum.TASK_STATUS_COMPLETED)
      void invalidateJobActivity()
    } catch (error) {
      reportError(error, { feature: 'job_task_complete', kind: 'mutation' })
      toast.error('Failed to complete task', { description: getErrorDescription(error) })
    } finally {
      setCompletingIds((ids) => {
        const next = new Set(ids)
        next.delete(taskId)
        return next
      })
    }
  }

  async function addTask(draft: JobTaskDraft) {
    if (!selectedOrganizationId) return
    try {
      await createMutation.mutateAsync(
        create(CreateTaskRequestSchema, {
          organizationId: selectedOrganizationId,
          parentType: parent.parentType,
          parentId: parent.parentId,
          assignedTo: draft.assignedTo,
          dueDate: dateToDateOnlyTimestamp(draft.dueDate),
          title: draft.title,
          description: draft.description,
        }),
      )
      void invalidateJobActivity()
    } catch (error) {
      reportError(error, { feature: 'job_task_create', kind: 'mutation' })
      toast.error('Failed to add task', { description: getErrorDescription(error) })
      // Keep the composer's text so the user can retry.
      throw error
    }
  }

  return (
    <Card className="gap-3 pt-4">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">{eventId ? 'Tasks for this event' : 'Open tasks'}</CardTitle>
        <span className="text-xs tabular-nums text-muted-foreground">{totalCount}</span>
      </CardHeader>
      <CardContent className="px-0 pb-0">
        {!hasLoaded ? (
          <div className="space-y-2 px-6 pb-4">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : openTasks.length === 0 ? (
          <p className="px-6 pb-3 text-sm text-muted-foreground">No open tasks.</p>
        ) : (
          <ul className="divide-y">
            {openTasks.map((task) => {
              const meta = jobTaskMetaParts(task.dueDate, formatFullName(task.assignedToFirstName, task.assignedToLastName))
              return (
                <li key={task.id} className="flex items-start gap-3 px-6 py-2.5">
                  <Checkbox
                    className="mt-0.5"
                    aria-label={`Complete ${task.title}`}
                    onCheckedChange={(checked) => {
                      if (checked === true) void completeTask(task.id)
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setSheet({ task: taskFromLite(task) })}
                    className="min-w-0 flex-1 rounded-sm text-left outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
                  >
                    <div className="text-sm font-medium">{task.title}</div>
                    {(meta.due || meta.assignee) && (
                      <div className="text-xs text-muted-foreground">
                        {meta.due && (
                          <span data-overdue={meta.overdue} className={cn(meta.overdue && 'text-destructive')}>
                            {meta.due}
                          </span>
                        )}
                        {meta.due && meta.assignee && ' · '}
                        {meta.assignee}
                      </div>
                    )}
                  </button>
                </li>
              )
            })}
          </ul>
        )}
        <div className="border-t px-6 py-2.5">
          {isAdding ? (
            <JobTaskComposer
              currentUserId={user?.id}
              eventDay={eventDay}
              isPending={createMutation.isPending}
              onCreate={addTask}
              onOpenFullForm={(draft) => setSheet({ draft })}
              onCancel={() => setIsAdding(false)}
            />
          ) : (
            <button
              type="button"
              onClick={() => setIsAdding(true)}
              className="flex items-center gap-1.5 rounded-sm text-sm text-muted-foreground outline-none hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50"
            >
              <Plus className="h-3.5 w-3.5" />
              Add a task
            </button>
          )}
        </div>
      </CardContent>
      {sheet && selectedOrganizationId && (
        <TaskSheet
          layout="pipeline"
          open
          organizationId={selectedOrganizationId}
          parentType={parent.parentType}
          parentId={parent.parentId}
          task={'task' in sheet ? sheet.task : undefined}
          initialValues={'draft' in sheet ? sheet.draft : undefined}
          lockedParentLabel={parent.label}
          onSaved={() => {
            void invalidateJobActivity()
            if ('draft' in sheet) setIsAdding(false)
          }}
          onOpenChange={(open) => {
            if (!open) setSheet(null)
          }}
        />
      )}
    </Card>
  )
}
